import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ReleaseConfiguration,
  WorkspaceRole,
} from "@insights/contracts";
import { api } from "../../../api";

type BuildConfigurationResponse = {
  configuration: ReleaseConfiguration;
  role: WorkspaceRole;
};

export function useBuildConfiguration(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ["build-configuration", workspaceId];
  const query = useQuery({
    enabled: Boolean(workspaceId),
    queryKey,
    queryFn: () =>
      api<BuildConfigurationResponse>(`/${workspaceId}/builds/configuration`),
  });
  const repositories = useQuery({
    enabled: Boolean(workspaceId),
    queryKey: ["release-profile-repositories", workspaceId],
    queryFn: () => api<string[]>(`/${workspaceId}/builds/profile-repositories`),
  });
  const update = useMutation({
    mutationFn: (configuration: ReleaseConfiguration) =>
      api<ReleaseConfiguration>(`/${workspaceId}/builds/configuration`, {
        method: "PUT",
        body: JSON.stringify(configuration),
      }),
    onSuccess: (configuration) => {
      queryClient.setQueryData<BuildConfigurationResponse>(queryKey, (current) =>
        current ? { ...current, configuration } : current,
      );
      void queryClient.invalidateQueries({
        queryKey: ["timeline-options", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["environment-timeline", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["release-notes-scopes", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["release-points", workspaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["release-notes", workspaceId],
      });
    },
  });

  return { query, update, repositories };
}
